/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { AlertCircle, Zap, Briefcase, Repeat, CheckCircle2, Coins, Shield, Globe, Lock } from 'lucide-react';

export const AboutGhostPay = () => (
  <div className="space-y-16 animate-fade-in">
    <section>
        <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-red-100 dark:bg-red-900/30 rounded-xl text-red-600 dark:text-red-400 shadow-inner"><AlertCircle size={24} /></div>
            <h3 className="text-3xl font-serif font-bold text-stone-900 dark:text-white">The Legacy Impasse</h3>
        </div>
        <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-light">
            Legacy finance is fundamentally incompatible with the digital age. Centralized processors charge invasive data "taxes" disguised as transaction fees, while holding merchant funds for days. We believe financial <span className="text-stone-900 dark:text-white font-bold italic">silence</span> and <span className="text-stone-900 dark:text-white font-bold italic">speed</span> are the twin pillars of future commerce.
        </p>
    </section>
    
    <section className="bg-stone-50 dark:bg-white/[0.02] p-8 rounded-card border border-stone-200 dark:border-white/10">
        <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-green-100 dark:bg-green-900/30 rounded-xl text-green-600 dark:text-green-400 shadow-inner"><CheckCircle2 size={24} /></div>
            <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">GhostOS Protocol</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
            <div className="space-y-3">
                <div className="w-10 h-10 bg-white dark:bg-white/5 rounded-lg flex items-center justify-center text-stone-900 dark:text-white shadow-sm"><Shield size={20}/></div>
                <h4 className="font-bold text-sm">Strict Privacy</h4>
                <p className="text-xs text-stone-500 leading-relaxed">No data harvesting. Every transaction is a shielded event verified by zero-knowledge logic in the GhostOS runtime.</p>
            </div>
            <div className="space-y-3">
                <div className="w-10 h-10 bg-white dark:bg-white/5 rounded-lg flex items-center justify-center text-stone-900 dark:text-white shadow-sm"><Globe size={20}/></div>
                <h4 className="font-bold text-sm">Global Rails</h4>
                <p className="text-xs text-stone-500 leading-relaxed">Operate in any jurisdiction without banking intermediaries. Pure on-chain finality across Solana and Sui.</p>
            </div>
            <div className="space-y-3">
                <div className="w-10 h-10 bg-white dark:bg-white/5 rounded-lg flex items-center justify-center text-stone-900 dark:text-white shadow-sm"><Lock size={20}/></div>
                <h4 className="font-bold text-sm">Self-Custody</h4>
                <p className="text-xs text-stone-500 leading-relaxed">GhostOS never touches your keys. You remain the sole arbiter of your capital at all times through hardware-level security.</p>
            </div>
        </div>
    </section>

    <section>
        <div className="flex justify-between items-end mb-8 border-b border-stone-100 dark:border-white/10 pb-4">
            <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">Commercial Model</h3>
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Fee Architecture</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
            {[
                { icon: Briefcase, title: "Standard Gateway", fee: "0.1%", desc: "The lowest fee in the industry for POS and API settlements on standard accounts." },
                { icon: Zap, title: "Enterprise OS", fee: "0%", desc: "Zero transaction fees for Pro and Sovereign members. Subscription-based elite access." },
                { icon: Repeat, title: "Dynamic Swaps", fee: "Tiered", desc: "Swap rates from 1.0% (Sovereign) to 2.5% (Merchant) via native DEX integrations." },
                { icon: Coins, title: "On-Chain Gas", fee: "Native", desc: "Network priority fees paid directly to validators for sub-second atomic finality." },
            ].map((item, i) => (
                <div key={i} className="p-6 bg-white dark:bg-white/[0.03] rounded-card border border-stone-200 dark:border-white/10 group hover:border-nobel-gold/50 transition-all">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <item.icon size={20} className="text-stone-400 group-hover:text-nobel-gold transition-colors" />
                            <h4 className="font-bold text-stone-900 dark:text-white">{item.title}</h4>
                        </div>
                        <span className="px-3 py-1 bg-stone-100 dark:bg-white/10 rounded-full text-[10px] font-bold uppercase tracking-widest text-stone-900 dark:text-white border border-stone-200 dark:border-white/10">{item.fee}</span>
                    </div>
                    <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">{item.desc}</p>
                </div>
            ))}
        </div>
    </section>
  </div>
);